// Calls & Callbacks Review (approved HTML:
// 16-Calls-Callbacks-Consistency-Review-v5.html).
//
// Persistent call workspace. Provider state (Kixie Business Id 72616)
// stays authoritative. Warm vs direct transfer, exact-operation unknown
// recovery, and Finish-held-until-disposition stay distinct. All writes
// blocked at runtime via LIVE_WRITE_GATE_KIXIE_*.
//
// W31 (2026-10-20): on a real session the breadcrumb, identity row and
// call dock come from the HubSpot profile (real name + phone, no
// invented call state); nothing from the fixture renders.
//
// W37b (2026-10-20): GET /v1/contacts/:id/calls answers 200 since W37a
// (sinks/calls-sink.mjs, READ; MEASURED for Jodi 573130771181:
// `{calls: [], callbacks: [{dueAt: "2026-11-18T00:00:00.000Z", source:
// "call_back_date_vs"}], kixie: {available: false, reason:
// "KIXIE_HISTORY_READ_NOT_AVAILABLE"}}`). The call history lists the
// HubSpot call engagements (empty -> "No calls logged in HubSpot yet"),
// the Requested Call card lists `callbacks[]` ("Callback requested for
// 2026-11-18") and the Kixie note states that Kixie call history is not
// available via API. A 503 still shows the honest error card.
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { WorkspaceChrome } from '../_shared/WorkspaceChrome.js';
import { DataBoundary, type BoundaryState } from '../_shared/DataBoundary.js';
import { IdentityRow } from '../_shared/IdentityRow.js';
import { CustomerTabs } from '../_shared/CustomerTabs.js';
import { Icon } from '../_shared/IconSprite.js';
import { ActionFooter } from '../_shared/ActionFooter.js';
import { RepriorityPill } from '../_shared/PriorityFocus.js';
import { QueueUpNext } from '../_shared/UpNext.js';
import { CallDock, PREVIEW_CALL_INFO, type CallDockInfo } from './components/CallDock.js';
import { ConversationViews } from './components/ConversationViews.js';
import { CALL_GUIDE, CONVERSATION_LABELS, NEXT_RECORD } from './mockData.js';
import type { ConversationView } from './mockData.js';
import { SARAH } from '../../data/mockData.js';
import { getReviewReturnTarget } from '../_shared/ReviewState.js';
import { useScopedData } from '../_w2a_lower/scopedData.js';
import {
  isRecord,
  monthYear,
  readOptionalString,
  readString,
  useScreenIdentity,
} from '../_w2a_lower/identity.js';
import { WriteButton } from '../_w2a_lower/liveWrite.js';
import './styles.css';

// Call session + requested callbacks for this customer. The notes draft
// stays local; dispositions and callback requests are not posted from
// the preview path.
interface CallsCallbacksFixture {
  callGuide: typeof CALL_GUIDE;
  conversationLabels: typeof CONVERSATION_LABELS;
  nextRecord: typeof NEXT_RECORD;
}

const CALLS_CALLBACKS_FIXTURE: CallsCallbacksFixture = {
  callGuide: CALL_GUIDE,
  conversationLabels: CONVERSATION_LABELS,
  nextRecord: NEXT_RECORD,
};

/**
 * One HubSpot call engagement from the 200 body. Row shape READ from
 * services/api-server/sinks/calls-sink.mjs callRow(): {id, at, direction,
 * durationSec, disposition, dispositionLabel, outcome, status, title,
 * body, ownerId, repEmail, repName, recordingUrl, source}.
 */
export interface RealCallRow {
  id: string;
  at: string | null;
  direction: string | null;
  outcome: string | null;
  durationSec: number | null;
  repEmail: string | null;
  repName: string | null;
  title: string | null;
  body: string | null;
}

/**
 * One requested callback from `callbacks[]`. READ from calls-sink.mjs
 * callbackFromProperty() / callbackFromTask(): {dueAt, source
 * ('call_back_date_vs' | 'hubspot-task'), subject, taskId, status, ownerId}.
 */
export interface RealCallbackRow {
  dueAt: string | null;
  source: string;
  subject: string | null;
  taskId: string | null;
}

export interface RealKixieState {
  /** null when the body carries no `kixie` object. */
  available: boolean | null;
  reason: string | null;
}

/**
 * W38 (2026-10-20): E.164 dial number from GET /v1/contacts/:id/phone
 * (`{ contactId, phone: "+1...", displayName, available }`, READ
 * routes/contact-phone.mjs; "+14067501531" MEASURED for Jodi). Null when
 * the body has no "+"-prefixed phone so the dock falls back to the raw
 * HubSpot profile phone.
 */
export function readE164Phone(body: unknown): string | null {
  if (!isRecord(body)) return null;
  const raw = body.phone;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.startsWith('+') ? trimmed : null;
}

export function mapRealCalls(body: unknown): RealCallRow[] {
  const list = isRecord(body) && Array.isArray(body.calls) ? body.calls : [];
  const out: RealCallRow[] = [];
  list.forEach((raw, index) => {
    if (!isRecord(raw)) return;
    const dur = raw.durationSec;
    out.push({
      id: readString(raw.id) || `call-${index + 1}`,
      at: readOptionalString(raw.at),
      direction: readOptionalString(raw.direction),
      // `outcome` is the disposition label; fall back to the HubSpot
      // call status so a row without a mapped disposition still says
      // something true.
      outcome: readOptionalString(raw.outcome) ?? readOptionalString(raw.dispositionLabel) ?? readOptionalString(raw.status),
      durationSec: typeof dur === 'number' && Number.isFinite(dur) ? dur : null,
      repEmail: readOptionalString(raw.repEmail),
      repName: readOptionalString(raw.repName),
      title: readOptionalString(raw.title),
      body: readOptionalString(raw.body),
    });
  });
  return out;
}

export function mapRealCallbacks(body: unknown): RealCallbackRow[] {
  const list = isRecord(body) && Array.isArray(body.callbacks) ? body.callbacks : [];
  const out: RealCallbackRow[] = [];
  for (const raw of list) {
    if (!isRecord(raw)) continue;
    out.push({
      dueAt: readOptionalString(raw.dueAt),
      source: readString(raw.source) || 'unknown',
      subject: readOptionalString(raw.subject),
      taskId: readOptionalString(raw.taskId),
    });
  }
  return out;
}

export function readKixieState(body: unknown): RealKixieState {
  const kixie = isRecord(body) && isRecord(body.kixie) ? body.kixie : null;
  if (!kixie) return { available: null, reason: null };
  return {
    available: typeof kixie.available === 'boolean' ? kixie.available : null,
    reason: readOptionalString(kixie.reason),
  };
}

/**
 * Calendar date (YYYY-MM-DD) of an ISO timestamp, read off the string so a
 * midnight-UTC HubSpot date (call_back_date_vs) never shifts a day in the
 * rep's timezone. Non-ISO input falls back to the locale month format.
 */
export function callbackDateLabel(iso: string | null): string | null {
  if (!iso) return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(iso);
  if (m) return m[1]!;
  return monthYear(iso);
}

function callbackSourceLabel(source: string): string {
  if (source === 'call_back_date_vs') return 'VanillaSoft callback date';
  if (source === 'hubspot-task') return 'HubSpot task';
  return source;
}

export interface CallsCallbacksScreenProps {
  stateOverride?: BoundaryState;
}

export function CallsCallbacksScreen({
  stateOverride,
}: CallsCallbacksScreenProps = {}): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const customerId = params.customerId ?? SARAH.id;
  const identity = useScreenIdentity(customerId);
  const scoped = useScopedData<unknown>({
    path: `/v1/contacts/${encodeURIComponent(customerId)}/calls`,
    fixture: CALLS_CALLBACKS_FIXTURE,
    loadingLabel: 'Loading call history…',
    errorTitle: 'Calls history is not connected yet',
    errorCode: 'CALLS_CALLBACKS_UNAVAILABLE',
    isEmpty: () => false,
    ...(stateOverride !== undefined ? { stateOverride } : {}),
  });
  const useFixture = scoped.previewAllowed;
  const calls = useMemo<RealCallRow[]>(
    () => (useFixture ? [] : mapRealCalls(scoped.data)),
    [useFixture, scoped.data],
  );
  const callbacks = useMemo<RealCallbackRow[]>(
    () => (useFixture ? [] : mapRealCallbacks(scoped.data)),
    [useFixture, scoped.data],
  );
  const kixie = useMemo<RealKixieState>(
    () => (useFixture ? { available: null, reason: null } : readKixieState(scoped.data)),
    [useFixture, scoped.data],
  );
  // W38: the Kixie dial number in E.164 for the dock; the hook's own
  // loading / error state is ignored because the profile phone is the
  // fallback, never a fixture number.
  const phoneRead = useScopedData<unknown>({
    path: `/v1/contacts/${encodeURIComponent(customerId)}/phone`,
    fixture: null,
    isEmpty: () => false,
  });
  const e164Phone = useMemo(
    () => (useFixture ? null : readE164Phone(phoneRead.data)),
    [useFixture, phoneRead.data],
  );
  const customer = identity.customer;
  const firstName = identity.firstName;
  const paused = identity.liveWritesPaused;
  const [view, setView] = useState<ConversationView>('active');
  const [notes, setNotes] = useState('');
  const [muted, setMuted] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [disposition, setDisposition] = useState<'connected' | 'noanswer' | 'voicemail' | ''>('');
  const [kind, setKind] = useState<BoundaryState['kind']>(
    stateOverride?.kind ?? 'default',
  );
  const state =
    stateOverride ??
    (scoped.state.kind === 'default' ? resolveState(kind, undefined) : scoped.state);
  const finishDisabled = !(callEnded && disposition);
  const returnTarget = getReviewReturnTarget(location.state, customerId);

  const helperLine = useMemo(() => {
    if (!callEnded) return 'End the call first to record the result.';
    if (!disposition) return 'Record the result before Finish & Next.';
    return 'Result confirmed · Ready for next customer.';
  }, [callEnded, disposition]);

  // Dock facts: fixture in preview; real profile otherwise. The phone is
  // the E.164 number from /v1/contacts/:id/phone when that route answers
  // (W38), else the HubSpot `phone` / `mobilephone` value from
  // /v1/contacts/:id.
  const dockInfo: CallDockInfo = useFixture
    ? PREVIEW_CALL_INFO
    : {
        providerLabel: 'Kixie · Business Id 72616',
        providerEvidence: 'Kixie call state not connected yet',
        state: 'No active call',
        duration: '',
        callerName: customer.fullName,
        callerNumber: e164Phone ?? (customer.phone || 'No phone on file'),
        initials: customer.initials,
        directionLabel: 'CUSTOMER PHONE',
      };
  const callGuide = useFixture
    ? CALLS_CALLBACKS_FIXTURE.callGuide
    : {
        title: 'Check in and log the call',
        prompt: `Confirm what ${firstName} is feeding today and log the call.`,
      };

  return (
    <WorkspaceChrome screen="calls" rail="my-work">
      {/* W40: the approved 16 has no breadcrumb row (MEASURED, 0 entries);
          the row stays only as the carrier of the preview label. */}
      {scoped.previewAllowed ? (
        <div className="breadcrumbs">
          <div>
            My work <span className="slash">/</span> <strong>{customer.fullName}</strong>{' '}
            <span className="slash">/</span> Calls &amp; Callbacks
          </div>
          <span className="preview">Design preview · Sample customer · Kixie preview only</span>
        </div>
      ) : null}
      <section className="cc-hero">
        <div>
          <span className="eyebrow">CUSTOMER CONVERSATION</span>
          <h1>Calls &amp; Callbacks</h1>
          <p>Stay with the customer. Keep the conversation, the result and the next step together.</p>
        </div>
        <div className="cc-hero-cue">
          <Icon id="help" />
          <div>
            <b>One conversation. A clear next step.</b>
            <span>Call results · Requested callbacks</span>
          </div>
        </div>
        {/* W40: approved pill, informational, on every session. */}
        <div className="shared-repriority-heading">
          <RepriorityPill className="shared-repriority" />
        </div>
      </section>
      <IdentityRow
        preferenceNote="The customer’s request guides this conversation."
        customer={customer}
        badgeLabel={useFixture ? 'Live call' : 'Calls & callbacks'}
        {...(identity.sinceLabel !== undefined ? { sinceLabel: identity.sinceLabel } : {})}
      />
      <CustomerTabs customerId={customerId} active="current" />
      <CallDock
        info={dockInfo}
        controlsPaused={paused}
        onEndCall={() => {
          setCallEnded(true);
          setDisposition(view === 'noanswer' ? 'noanswer' : view === 'voicemail' ? 'voicemail' : 'connected');
        }}
        onTransfer={() => undefined}
        onMute={() => setMuted(!muted)}
        muted={muted}
      />
      <DataBoundary state={state} testId="calls-callbacks-boundary">
        <ConversationViews value={view} onChange={setView} onOpenHistory={() => undefined} />
        <div className="cc-grid">
          <div className="cc-stack">
            <section className="card cc-card cc-conversation-card">
              <div className="cc-card-head">
                <div>
                  <Icon id="help" />
                  <h2 id="cc-conversation-title">{CONVERSATION_LABELS[view]}</h2>
                </div>
                <WriteButton
                  paused={paused}
                  className="cc-cs"
                  id="cc-cs"
                  actionLabel="Create CS Ticket"
                >
                  Create CS Ticket
                </WriteButton>
              </div>
              <div className="cc-conversation-body">
                {view === 'unknown' ? (
                  <div className="cc-unknown-context" id="cc-unknown-context">
                    <div>
                      <Icon id="people" />
                      <strong>Identity not verified</strong>
                    </div>
                    <p>Start with the caller’s question. Verify identity before opening account details.</p>
                    <div className="cc-fields">
                      <label>
                        Name shared by caller <span className="cc-optional">Optional</span>
                        <input id="cc-caller-name" autoComplete="off" placeholder="Name shared by caller" />
                      </label>
                      <label>
                        Phone number <span className="cc-optional">Optional</span>
                        <input
                          type="tel"
                          id="cc-caller-phone"
                          autoComplete="off"
                          defaultValue={useFixture ? '(615) 555-0189' : ''}
                        />
                      </label>
                    </div>
                    <button className="cc-secondary" id="cc-verify-identity" type="button">
                      Check identity
                    </button>
                    <span className="cc-guest-note">
                      You can keep helping the caller without an account match.
                    </span>
                  </div>
                ) : (
                  <section className="cc-call-guide" id="cc-call-topic" aria-label="Call guide">
                    <div className="cc-guide-head">
                      <div>
                        <Icon id="message" />
                        <strong>Call guide</strong>
                      </div>
                      <button className="cc-link" id="cc-full-guide" type="button">
                        Details
                      </button>
                    </div>
                    <div className="cc-guide-inner" id="cc-guide-prompt">
                      <p className="cc-guide-reason" id="cc-guide-title">
                        {callGuide.title}
                      </p>
                      <p className="cc-guide-primary" id="cc-guide-prompt-copy">
                        {callGuide.prompt}
                      </p>
                    </div>
                  </section>
                )}
                {!useFixture ? (
                  <section className="cc-call-guide" aria-label="Call history" data-testid="calls-history">
                    <div className="cc-guide-head">
                      <div>
                        <Icon id="clock" />
                        <strong>Call history</strong>
                      </div>
                    </div>
                    {calls.length === 0 ? (
                      <p className="muted" role="status" data-testid="calls-history-empty">
                        No calls logged in HubSpot yet
                      </p>
                    ) : (
                      <ol data-testid="calls-history-list">
                        {calls.map(row => (
                          <li key={row.id} data-testid="calls-history-item">
                            <strong>{row.at ? monthYear(row.at) ?? row.at : 'Date unknown'}</strong>
                            {row.direction ? ` · ${row.direction}` : ''}
                            {row.outcome ? ` · ${row.outcome}` : ''}
                            {row.durationSec !== null ? ` · ${row.durationSec}s` : ''}
                            {row.repName || row.repEmail ? ` · ${row.repName ?? row.repEmail}` : ''}
                            {row.title ? <div>{row.title}</div> : null}
                            {row.body ? <small>{row.body}</small> : null}
                          </li>
                        ))}
                      </ol>
                    )}
                    {kixie.available === false ? (
                      <p className="cc-callback-note" data-testid="calls-kixie-note">
                        Kixie call history is not available via API
                        {kixie.reason ? ` (${kixie.reason})` : ''}. Calls Kixie logs into HubSpot
                        appear in the list above.
                      </p>
                    ) : null}
                  </section>
                ) : null}
                <label className="cc-field">
                  Conversation notes
                  <textarea
                    id="cc-call-notes"
                    rows={3}
                    placeholder="Record the customer’s questions and what was discussed"
                    value={notes}
                    onChange={event => setNotes(event.target.value)}
                  />
                </label>
                {view === 'voicemail' ? (
                  <label className="cc-field" id="cc-voicemail-label">
                    Rep-reported voicemail
                    <select id="cc-voicemail-report">
                      <option value="none">No message left</option>
                      <option value="left">Voicemail left</option>
                    </select>
                  </label>
                ) : null}
                <div className="cc-editor-bottom">
                  <p id="cc-call-save-hint">{helperLine}</p>
                </div>
              </div>
            </section>
          </div>
          <aside className="cc-stack">
            <section className="card cc-card">
              <div className="cc-card-head">
                <div>
                  <Icon id="clock" />
                  <h2>Requested Call</h2>
                </div>
              </div>
              <div className="cc-body">
                {callbacks.length > 0 ? (
                  <div id="cc-callback-summary" className="cc-callback-summary" data-testid="calls-callbacks">
                    <span className="cc-empty-icon">
                      <Icon id="clock" />
                    </span>
                    <strong>
                      {callbacks.length === 1 ? 'Callback requested' : `${callbacks.length} callbacks requested`}
                    </strong>
                    <ul>
                      {callbacks.map((row, index) => (
                        <li key={row.taskId ?? `${row.source}-${index}`} data-testid="calls-callback-item">
                          {row.dueAt
                            ? `Callback requested for ${callbackDateLabel(row.dueAt)}`
                            : 'Callback requested (no date)'}
                          {row.subject ? ` · ${row.subject}` : ''}
                          <small> · {callbackSourceLabel(row.source)}</small>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div id="cc-callback-summary" className="cc-callback-summary">
                    <span className="cc-empty-icon">
                      <Icon id="clock" />
                    </span>
                    <strong>No call requested</strong>
                    <p>
                      {identity.callBackDate
                        ? `VanillaSoft callback date: ${identity.callBackDate}`
                        : 'Record the agreed time and what the customer needs.'}
                    </p>
                  </div>
                )}
                <WriteButton
                  paused={paused}
                  className="cc-secondary cc-wide"
                  id="cc-add-callback"
                  actionLabel="Requested Call"
                >
                  <Icon id="clock" />
                  Requested Call
                </WriteButton>
                <p className="cc-callback-note">
                  AI determines the follow-up sequence from the most important open items.
                </p>
              </div>
            </section>
            <section className="cc-callout">
              <div className="cc-callout-icon">
                <Icon id="check" />
              </div>
              <div>
                <strong id="cc-contact-title">Keep the result clear</strong>
                <p id="cc-contact-copy">
                  Record the provider result and your next step before finishing.
                </p>
              </div>
            </section>
          </aside>
        </div>
        {useFixture ? (
          <section className="next cc-next" aria-label="Next action">
            <span className="eyebrow">UP NEXT</span>
            <div className="avatar">{CALLS_CALLBACKS_FIXTURE.nextRecord.initials}</div>
            <div>
              <strong>{CALLS_CALLBACKS_FIXTURE.nextRecord.name}</strong>
              <p>{CALLS_CALLBACKS_FIXTURE.nextRecord.description}</p>
            </div>
            <div className="reason">
              <span>Next record</span>
              <p>Keep the work moving</p>
            </div>
            <Icon id="arrow" />
          </section>
        ) : <QueueUpNext customerId={customerId} />}
      </DataBoundary>
      <ActionFooter
        disabled={finishDisabled}
        noteWhenDisabled={helperLine}
        noteWhenEnabled="Result confirmed · Ready for next customer"
        summaryStrong={`${customer.fullName} · Call result`}
        summaryBody="Record the call result before advancing."
        onFinish={() => navigate(returnTarget.path, { state: returnTarget.state })}
      />
      {scoped.previewAllowed && !stateOverride ? (
        <StatePreview kind={kind} onChange={setKind} />
      ) : null}
    </WorkspaceChrome>
  );
}

function resolveState(
  kind: BoundaryState['kind'],
  override: BoundaryState | undefined,
): BoundaryState {
  if (override) return override;
  switch (kind) {
    case 'default':
      return { kind: 'default' };
    case 'loading':
      return { kind: 'loading', label: 'Connecting to call…' };
    case 'empty':
      return {
        kind: 'empty',
        title: 'No active call',
        body: 'Dial from the customer record to start a conversation.',
      };
    case 'error':
      return {
        kind: 'error',
        title: 'Call provider unavailable',
        detail: 'Kixie Business Id 72616 did not respond.',
        errorCode: 'KIXIE_PROVIDER_UNAVAILABLE',
      };
    case 'degraded':
      return {
        kind: 'degraded',
        title: 'Partial data',
        regions: [{ region: 'cc-callback-summary', reason: 'followup_cache_stale' }],
      };
    case 'blocked':
      return {
        kind: 'blocked',
        title: 'Click-to-call is paused',
        reason: 'Kixie click-to-call gate OFF.',
        gateName: 'LIVE_WRITE_GATE_KIXIE_CLICK_TO_CALL',
      };
    case 'midcall':
      return {
        kind: 'midcall',
        label: 'Transferring call…',
        actionName: 'kixie_transfer',
      };
    default:
      return { kind: 'default' };
  }
}

const BOUNDARY_KINDS: Array<BoundaryState['kind']> = [
  'default',
  'loading',
  'empty',
  'error',
  'degraded',
  'blocked',
  'midcall',
];

function StatePreview({
  kind,
  onChange,
}: {
  kind: BoundaryState['kind'];
  onChange: (k: BoundaryState['kind']) => void;
}): ReactNode {
  return (
    <div
      aria-label="Companion state preview"
      style={{
        marginTop: 16,
        padding: 10,
        border: '1px dashed #c4d4e5',
        borderRadius: 6,
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        background: '#f7fbff',
      }}
    >
      <span style={{ fontSize: 9, letterSpacing: 1, color: '#557193', fontWeight: 700 }}>
        PREVIEW STATE:
      </span>
      {BOUNDARY_KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          aria-pressed={kind === k}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            borderRadius: 4,
            border: '1px solid #c4d4e5',
            background: kind === k ? '#175fac' : '#fff',
            color: kind === k ? '#fff' : '#305780',
            cursor: 'pointer',
          }}
        >
          {k}
        </button>
      ))}
    </div>
  );
}

export default CallsCallbacksScreen;
